import React from 'react';
import { cn } from '../../utils/cn';
import { useThemeColors } from '../../providers/ThemeProvider';

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  selected?: boolean;
  dragging?: boolean;
  hoverable?: boolean;
  padding?: 'none' | 'sm' | 'md' | 'lg';
}

interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  title: string | React.ReactNode;
  subtitle?: string;
  action?: React.ReactNode;
  actions?: React.ReactNode; // Keep for backward compatibility
}

interface CardBodyProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  padding?: 'none' | 'sm' | 'md' | 'lg';
}

interface CardFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  variant?: 'default' | 'gray';
}

const paddingClasses = {
  none: '',
  sm: 'p-4',
  md: 'p-6',
  lg: 'p-8'
};

export function Card({ 
  children, 
  onClick,
  selected = false, 
  dragging = false,
  hoverable = true,
  padding = 'none',
  className,
  ...htmlProps 
}: CardProps) {
  const colors = useThemeColors();
  return (
    <div
      onClick={onClick}
      className={cn(
        'rounded-lg border transition-all duration-200',
        colors.classes.bgPrimary,
        {
          [cn(colors.classes.borderDefault, 'shadow-sm')]: !selected && !dragging,
          'hover:shadow-md': hoverable && !selected && !dragging,
          [cn('shadow-md ring-2 ring-opacity-50')]: selected,
          'opacity-50 rotate-3 scale-105': dragging,
          'cursor-pointer': onClick
        },
        paddingClasses[padding],
        className
      )}
      style={selected ? {
        borderColor: colors.values.primary,
        '--tw-ring-color': colors.values.primary + '80'
      } as React.CSSProperties : undefined}
      {...htmlProps}
    >
      {children}
    </div>
  );
}

export function CardHeader({
  title,
  subtitle,
  action,
  actions,
  className,
  ...htmlProps
}: CardHeaderProps) {
  const colors = useThemeColors();
  const actionContent = action || actions; // Support both prop names

  return (
    <div
      className={cn('px-6 py-4 border-b', colors.classes.borderDefault, className)}
      {...htmlProps}
    >
      <div className="flex items-center justify-between">
        <div>
          {typeof title === 'string' ? (
            <h3 className={cn("text-lg font-medium", colors.classes.textPrimary)}>{title}</h3>
          ) : (
            <div className={cn("text-lg font-medium", colors.classes.textPrimary)}>{title}</div>
          )}
          {subtitle && (
            <p className={cn("mt-1 text-sm", colors.classes.textSecondary)}>{subtitle}</p>
          )}
        </div>
        {actionContent && (
          <div className="flex items-center space-x-2">{actionContent}</div>
        )}
      </div>
    </div>
  );
}

export function CardBody({ 
  children, 
  padding = 'md',
  className,
  ...htmlProps 
}: CardBodyProps) {
  return (
    <div 
      className={cn(paddingClasses[padding], className)}
      {...htmlProps}
    >
      {children}
    </div>
  );
}

export function CardFooter({ 
  children,
  variant = 'default',
  className,
  ...htmlProps 
}: CardFooterProps) {
  const colors = useThemeColors();
  return (
    <div 
      className={cn(
        'px-6 py-3 border-t rounded-b-lg',
        colors.classes.borderDefault,
        variant === 'gray' && colors.classes.bgSecondary,
        className
      )}
      {...htmlProps}
    >
      {children}
    </div>
  );
}